<template>
  <segmentation-dashboard
    :summary="segmentSummary"
    :insights="segmentInsights"
    :segment-stats="segmentStats.segments"
    :segment-tags="segmentTags.segments"
    :listeners="filterListeners"
    :aggregate="aggregateListeners"
    :cross-channel="crossChannelEngagement"
    :selected-listener="selectedListenerProfile"
    :recommendation-result="recommendationResult"
    :base-aggregate="baseAggregate"
    :is-search="!!searchQuery"
    :completeness="profileCompleteness"
    :recommend-error="recommendError"
    :credits-exhausted="creditsExhausted"
    :enriching="enriching"
    :enrich-credits-exhausted="enrichCreditsExhausted"
    :segment-freshness="segmentFreshness"
    :refreshing="refreshing"
    :loading-completeness="$apollo.queries.profileCompleteness.loading"
    :loading-summary="$apollo.queries.segmentSummary.loading"
    :load-total="loadNames.length"
    :load-done="loadNames.length - pendingLoads.length"
    :load-errors="loadErrorMessages"
    :segmentation-enabled="segmentationEnabled || !pluginsKnown"
    :loading-insights="$apollo.queries.segmentInsights.loading"
    :loading-listeners="$apollo.queries.filterListeners.loading"
    :loading-aggregate="$apollo.queries.aggregateListeners.loading"
    @refresh="handleRefresh"
    @filter="handleFilter"
    @load-more="handleLoadMore"
    @recommend="handleRecommend"
    @recommend-cleared="handleRecommendCleared"
    @recommendation-used="handleRecommendationUsed"
    @enrich="handleEnrich"
    @find-similar="handleFindSimilar"
    @apply-inferred="handleApplyInferred"
    @review-link="handleReviewLink"
    @replace-demographic="handleReplaceDemographic"
    @deploy="handleDeploy"
    @open-listener="handleOpenListener"
    @close-listener="handleCloseListener"
    @view-profile="handleViewProfile"
  />
</template>

<script>
import SegmentationDashboard from '@/components/segmentation/Dashboard';
import segmentsQuery from '@/graphql/segmentation/segments/segmentsQuery.gql';
import segmentFreshnessQuery from '@/graphql/segmentation/segments/segmentFreshnessQuery.gql';
import refreshStationDataMutation from '@/graphql/segmentation/segments/refreshStationDataMutation.gql';
import segmentSummaryQuery from '@/graphql/segmentation/segments/segmentSummaryQuery.gql';
import segmentInsightsQuery from '@/graphql/segmentation/segments/segmentInsightsQuery.gql';
import filterListenersQuery from '@/graphql/segmentation/listeners/filterListenersQuery.gql';
import aggregateListenersQuery from '@/graphql/segmentation/analytics/aggregateListenersQuery.gql';
import crossChannelEngagementQuery from '@/graphql/segmentation/analytics/crossChannelEngagementQuery.gql';
import listenerProfileQuery from '@/graphql/segmentation/listeners/listenerProfileQuery.gql';
import recommendCampaignQuery from '@/graphql/segmentation/ai/recommendCampaignQuery.gql';
import markRecommendationUsedMutation from '@/graphql/segmentation/ai/markRecommendationUsedMutation.gql';
import similarListenersQuery from '@/graphql/segmentation/listeners/similarListenersQuery.gql';
import profileCompletenessQuery from '@/graphql/segmentation/analytics/profileCompletenessQuery.gql';
import aiListenerProfileQuery from '@/graphql/segmentation/ai/aiListenerProfileQuery.gql';
import promoteDemographicsMutation from '@/graphql/segmentation/ai/promoteDemographicsMutation.gql';
import replaceDemographicMutation from '@/graphql/segmentation/ai/replaceDemographicMutation.gql';
import reviewLinkedIdentifierMutation from '@/graphql/segmentation/listeners/reviewLinkedIdentifierMutation.gql';
import {
  isCreditsError,
  featureLimitMessage,
  isNotFoundError,
  LISTENER_FETCH_SIZE,
  toPromoteField,
} from '@/components/segmentation/utils/segmentation';

/**
 * The message to show for a failed load: the service's own text when there
 * is one, otherwise a plain description of a network failure.
 */
const errorText = e => {
  const gql = (e && e.graphQLErrors) || [];
  if (gql.length && gql[0].message) {
    return gql[0].message;
  }
  if (e && e.networkError) {
    return 'Could not reach the server. Please check your connection and try again.';
  }
  return (e && e.message) || 'Something went wrong while loading segmentation data.';
};

/**
 * How often the dashboard asks whether the station's data has changed, while
 * the tab is open. The answer is a stored result on the server, so asking is
 * cheap.
 */
const AUTO_REFRESH_MS = 60 * 1000;

/**
 * The dashboard queries that are reloaded quietly when new data arrives. The
 * listener list is left alone so it does not change under someone who is
 * reading it.
 */
const AUTO_REFRESH_QUERIES = [
  'segmentSummary',
  'segmentInsights',
  'segmentTags',
  'segmentStats',
  'aggregateListeners',
  'crossChannelEngagement',
  'profileCompleteness',
];

/**
 * The queries the first load waits for, so the dashboard can tell "still
 * loading" from "no data" and report every failure in one place.
 */
const LOAD_NAMES = [
  'segmentSummary',
  'segmentFreshness',
  'segmentInsights',
  'segmentTags',
  'segmentStats',
  'filterListeners',
  'aggregateListeners',
  'crossChannelEngagement',
  'profileCompleteness',
];

/**
 * Hooks that report a query as loaded, or as failed with its message.
 */
const trackLoad = name => ({
  result({ loading }) {
    if (!loading) {
      this.loadSettled(name);
    }
  },
  error(e) {
    this.loadFailed(name, e);
  },
});

export default {
  name: 'SegmentationDashboardContainer',
  components: { SegmentationDashboard },

  data: () => ({
    page: 1,
    searchQuery: '',
    activeFilters: { page: 1, page_size: LISTENER_FETCH_SIZE },
    loadingMore: false,
    recommendationResult: null,
    selectedListenerProfile: null,
    recommendError: false,
    creditsExhausted: false,
    enriching: false,
    enrichCreditsExhausted: false,
    refreshing: false,

    loadNames: LOAD_NAMES,

    /**
     * The queries the first load is still waiting on, and the message of
     * each one that failed (by query name).
     */
    pendingLoads: [...LOAD_NAMES],
    loadErrors: {},

    segmentFreshness: {
      data_updated_at: null,
      segments: {},
    },

    segmentSummary: {
      totalListeners: 0,
      highEngagementCount: 0,
      segmentCounts: {},
    },

    segmentInsights: {
      totalListeners: 0,
      summary: null,
      segments: [],
    },

    segmentTags: {
      segments: {},
    },

    segmentStats: {
      totalListeners: 0,
      segments: [],
    },

    filterListeners: {
      totalMatches: 0,
      page: 1,
      pageSize: LISTENER_FETCH_SIZE,
      listeners: [],
    },

    aggregateListeners: {
      total: 0,
      channelCounts: {},
      genderCounts: {},
      ageGroupCounts: {},
      languageCounts: {},
      locationCounts: {},
      segmentCounts: {},
      momentumCounts: {},
      campaignTypeCounts: {},
    },

    crossChannelEngagement: {
      total: 0,
      channels: [],
      uniques: {},
      matrix: {},
      percentages: {},
    },

    baseAggregate: {
      total: 0,
      channelCounts: {},
      genderCounts: {},
      ageGroupCounts: {},
      languageCounts: {},
      locationCounts: {},
    },

    profileCompleteness: {
      total: 0,
      completeCount: 0,
      incompleteCount: 0,
      missingFieldCounts: {},
    },
  }),

  apollo: {
    segmentSummary: {
      client: 'apiv2',
      query: segmentSummaryQuery,
      skip() {
        return !this.segmentationEnabled;
      },
      ...trackLoad('segmentSummary'),
    },

    segmentFreshness: {
      client: 'apiv2',
      query: segmentFreshnessQuery,
      fetchPolicy: 'network-only',
      update: data => data.segmentFreshness,
      skip() {
        return !this.segmentationEnabled;
      },
      ...trackLoad('segmentFreshness'),
    },

    segmentInsights: {
      client: 'apiv2',
      query: segmentInsightsQuery,
      update: data => data.segmentInsights,
      skip() {
        return !this.segmentationEnabled;
      },
      ...trackLoad('segmentInsights'),
    },
    segmentTags: {
      client: 'apiv2',
      query: segmentInsightsQuery,
      update: data => data.segmentTags,
      skip() {
        return !this.segmentationEnabled;
      },
      ...trackLoad('segmentTags'),
    },

    segmentStats: {
      client: 'apiv2',
      query: segmentsQuery,
      update: data => data.segments,
      skip() {
        return !this.segmentationEnabled;
      },
      ...trackLoad('segmentStats'),
    },

    filterListeners: {
      client: 'apiv2',
      query: filterListenersQuery,
      fetchPolicy: 'network-only',
      skip() {
        return !this.segmentationEnabled;
      },
      variables() {
        return {
          params: {
            ...this.activeFilters,
            ...(this.searchQuery ? { originator: this.searchQuery } : {}),
          },
        };
      },
      result: trackLoad('filterListeners').result,
      error(err) {
        this.loadFailed('filterListeners', err);
        const isAuthError = (err.graphQLErrors || []).some(
          e => e.message && e.message.includes('resolve context'),
        );
        if (isAuthError) {
          this.activeFilters = { page: 1, page_size: LISTENER_FETCH_SIZE };
        }
      },
    },

    aggregateListeners: {
      client: 'apiv2',
      query: aggregateListenersQuery,
      skip() {
        return !this.segmentationEnabled;
      },
      variables() {
        return { params: this.activeFilters };
      },
      ...trackLoad('aggregateListeners'),
    },

    crossChannelEngagement: {
      client: 'apiv2',
      query: crossChannelEngagementQuery,
      skip() {
        return !this.segmentationEnabled;
      },
      variables() {
        return { params: this.activeFilters };
      },
      ...trackLoad('crossChannelEngagement'),
    },

    profileCompleteness: {
      client: 'apiv2',
      query: profileCompletenessQuery,
      skip() {
        return !this.segmentationEnabled;
      },
      variables() {
        return { params: this.activeFilters };
      },
      ...trackLoad('profileCompleteness'),
    },
  },

  watch: {
    aggregateListeners(val) {
      if (val && val.total && !this.baseAggregate.total) {
        this.baseAggregate = { ...val };
      }
    },

    /**
     * New data has arrived when the station's data-updated time moves on.
     *
     * Note: Not while a manual refresh is running (it reloads everything
     * itself), and not for the first answer.
     */
    segmentFreshness(val, old) {
      const before = old && old.data_updated_at;
      if (before && val && val.data_updated_at !== before && !this.refreshing) {
        this.reloadQuietly();
      }
    },
  },

  created() {
    this.quietCount = 0;
    this.autoRefreshTimer = setInterval(this.checkForNewData, AUTO_REFRESH_MS);
    document.addEventListener('visibilitychange', this.checkForNewData);
  },

  beforeDestroy() {
    clearInterval(this.autoRefreshTimer);
    document.removeEventListener('visibilitychange', this.checkForNewData);
  },

  computed: {
    /**
     * The distinct messages of the queries that failed, as the service
     * worded them.
     */
    loadErrorMessages() {
      return [...new Set(Object.values(this.loadErrors))];
    },

    /**
     * Whether the station's plugin list has been answered yet.
     */
    pluginsKnown() {
      const { loadingPluginsSucceeded, loadingPluginsFailed } = this.$store.state.plugin;
      return !!(loadingPluginsSucceeded || loadingPluginsFailed);
    },

    /**
     * Note: Until the plugin list arrives this is false, which is not the
     * same as "not enabled", so the dashboard is told `pluginsKnown` too.
     * If the list could not be loaded the queries run and report their own
     * errors rather than claiming the plugin is off.
     */
    segmentationEnabled() {
      if (this.$store.state.plugin.loadingPluginsFailed) {
        return true;
      }
      const plugins = this.$store.state.plugin.plugins || [];
      return plugins.some(plugin => plugin.plugin_route === 'app.profiles.segmentation');
    },
  },

  methods: {
    /**
     * Asks the server whether the station's data has changed.
     *
     * Note: It only asks while the tab is showing, when the dashboard is
     * switched on for the station, and after the first load, so a hidden tab
     * costs nothing. If it fails nothing happens: the next check tries again.
     */
    checkForNewData() {
      if (document.hidden || !this.segmentationEnabled || this.pendingLoads.length) {
        return;
      }
      this.quietly(this.$apollo.queries.segmentFreshness.refetch());
    },

    /**
     * Waits for a background reload without ever reporting its failure to the
     * station: a failed check should not pop up an error over their work.
     */
    quietly(promise) {
      this.quietCount += 1;
      return promise
        .catch(() => null)
        .then(() => {
          this.quietCount -= 1;
        });
    },

    /**
     * Reloads the dashboard's numbers without any loading screen.
     */
    reloadQuietly() {
      AUTO_REFRESH_QUERIES.forEach(name => {
        const query = this.$apollo.queries[name];
        if (query) {
          this.quietly(query.refetch());
        }
      });
    },

    loadSettled(name) {
      this.pendingLoads = this.pendingLoads.filter(n => n !== name);
      if (this.loadErrors[name]) {
        const { [name]: cleared, ...rest } = this.loadErrors;
        this.loadErrors = rest;
      }
    },

    /**
     * Note: A failed query is also counted as finished, so the loading
     * pop-up gives way to the error instead of waiting forever.
     */
    loadFailed(name, e) {
      if (this.quietCount > 0) {
        return;
      }
      this.pendingLoads = this.pendingLoads.filter(n => n !== name);
      this.loadErrors = { ...this.loadErrors, [name]: errorText(e) };
    },

    /**
     * Drops the station's saved results on the server, then reloads every
     * dashboard query so the numbers come from live data.
     */
    async handleRefresh() {
      if (this.refreshing) {
        return;
      }
      this.refreshing = true;
      try {
        await this.$apollo.mutate({ client: 'apiv2', mutation: refreshStationDataMutation });
        await Promise.all(
          Object.values(this.$apollo.queries).map(q => q.refetch().catch(() => null)),
        );
        this.$notify({ type: 'success', message: 'Dashboard data refreshed.' });
      } catch (e) {
        this.$notify({ type: 'error', message: 'Could not refresh right now. Please try again.' });
      } finally {
        this.refreshing = false;
      }
    },

    handleFilter(params) {
      this.page = 1;
      const sanitised = this.sanitiseParams(params);

      if (sanitised.originator) {
        this.searchQuery = sanitised.originator;
        delete sanitised.originator;
      } else {
        this.searchQuery = '';
      }

      this.activeFilters = {
        ...sanitised,
        page: 1,
        page_size: LISTENER_FETCH_SIZE,
      };
    },

    /**
     * Appends the next batch of listeners. Called by the browser when the
     * visitor is within a couple of pages of the end of what is loaded.
     *
     * The next API page is derived from how many listeners are already
     * loaded (not a running counter), and only one request runs at a time,
     * so repeated calls can never skip or double-fetch a batch.
     */
    async handleLoadMore() {
      const loaded = (this.filterListeners.listeners || []).length;
      if (this.loadingMore || loaded >= (this.filterListeners.totalMatches || 0)) {
        return;
      }

      this.loadingMore = true;
      this.page = Math.floor(loaded / LISTENER_FETCH_SIZE) + 1;

      try {
        await this.$apollo.queries.filterListeners.fetchMore({
          variables: {
            params: {
              ...this.activeFilters,
              ...(this.searchQuery ? { originator: this.searchQuery } : {}),
              page: this.page,
              page_size: LISTENER_FETCH_SIZE,
            },
          },
          updateQuery(prev, { fetchMoreResult }) {
            if (!fetchMoreResult) {
              return prev;
            }
            // Never show the same listener twice if the result set shifted
            const seen = new Set(prev.filterListeners.listeners.map(l => l.originator));
            const fresh = fetchMoreResult.filterListeners.listeners.filter(
              l => !seen.has(l.originator),
            );
            return {
              filterListeners: {
                ...fetchMoreResult.filterListeners,
                listeners: [...prev.filterListeners.listeners, ...fresh],
              },
            };
          },
        });
      } catch (e) {
        this.$notify({ type: 'error', message: 'Could not load more listeners.' });
      } finally {
        this.loadingMore = false;
      }
    },

    async handleOpenListener(listener, { fallback = true } = {}) {
      try {
        const { data } = await this.$apollo.query({
          client: 'apiv2',
          query: listenerProfileQuery,
          variables: { originator: listener.originator },
          fetchPolicy: 'network-only',
        });
        this.selectedListenerProfile = data.listenerProfile;
      } catch (e) {
        if (fallback) {
          this.selectedListenerProfile = listener;
        }
      }
    },

    /**
     * Opens another listener's profile, such as a linked one.
     *
     * Note: Says so when the profile cannot be opened. It used to fail
     * silently, which looked like the click did nothing.
     */
    async handleViewProfile(originator) {
      await this.handleOpenListener({ originator }, { fallback: false });
      const opened = this.selectedListenerProfile;
      if (!opened || opened.originator !== originator) {
        this.$notify({ type: 'warning', message: 'Could not open that profile.' });
      }
    },

    handleCloseListener() {
      this.selectedListenerProfile = null;
    },

    handleRecommend({ brief, filters }) {
      this.creditsExhausted = false;
      this.$apollo
        .query({
          client: 'apiv2',
          query: recommendCampaignQuery,
          variables: { brief, params: filters },
          fetchPolicy: 'network-only',
        })
        .then(({ data }) => {
          this.recommendationResult = data.recommendCampaign;
        })
        .catch(e => {
          if (isCreditsError(e)) {
            this.creditsExhausted = true;
            return;
          }
          this.$notify({
            type: 'error',
            message: featureLimitMessage(e) || 'Failed to generate recommendation.',
          });
          this.recommendError = true;
          setTimeout(() => {
            this.recommendError = false;
          }, 100);
        });
    },

    /**
     * Tells the server a recommendation was acted on. A failure is ignored:
     * the export it came with must not be held up by the record.
     */
    handleRecommendationUsed({ id, action }) {
      this.$apollo
        .mutate({
          client: 'apiv2',
          mutation: markRecommendationUsedMutation,
          variables: { recommendationId: id, action },
        })
        .catch(() => {});
    },

    handleRecommendCleared() {
      this.creditsExhausted = false;
      this.recommendationResult = null;
    },

    /**
     * Enriches one listener.
     *
     * Note: The server refuses a listener who has not given permission and
     * says so, then the station is asked. Only "Enrich anyway" sends the
     * override, which the server records.
     */
    async handleEnrich(listener) {
      this.enrichCreditsExhausted = false;
      this.enriching = true;

      try {
        let { data } = await this.$apollo.query({
          client: 'apiv2',
          query: aiListenerProfileQuery,
          variables: { originator: listener.originator, includeUnconsented: false },
          fetchPolicy: 'network-only',
        });
        if (data.aiListenerProfile.consent_required) {
          if (!(await this.confirmUnconsented(listener.originator))) {
            this.$notify({
              type: 'info',
              message: `${listener.originator} was not enriched: they haven't given permission.`,
            });
            return;
          }
          ({ data } = await this.$apollo.query({
            client: 'apiv2',
            query: aiListenerProfileQuery,
            variables: { originator: listener.originator, includeUnconsented: true },
            fetchPolicy: 'network-only',
          }));
        }
        await this.handleOpenListener({ originator: listener.originator }, { fallback: false });
        this.$notify({
          type: 'success',
          message: `${listener.originator} profile enriched successfully`,
        });
      } catch (e) {
        if (isCreditsError(e)) {
          this.enrichCreditsExhausted = true;
        } else {
          this.$notify({
            type: 'error',
            message:
              featureLimitMessage(e) ||
              (isNotFoundError(e)
                ? 'This listener has no profile on record that can be enriched.'
                : 'Failed to enrich this profile.'),
          });
        }
      } finally {
        this.enriching = false;
      }
    },

    /**
     * Asks whether to enrich a listener who has not given permission.
     * Resolves true only for "Enrich anyway".
     *
     * Note: Closing or cancelling the box rejects, which means "no".
     */
    async confirmUnconsented(originator) {
      try {
        await this.$confirm(
          `${originator} hasn't given permission for their data to be processed.`,
          'Permission not given',
          {
            type: 'warning',
            confirmButtonText: 'Enrich anyway',
            cancelButtonText: "Don't enrich",
          },
        );
        return true;
      } catch (e) {
        /* Ignored on purpose: see the Note above the function. */
        return false;
      }
    },

    async handleFindSimilar({ originator, limit = 10, minScore = 0.3, callback }) {
      try {
        const { data } = await this.$apollo.query({
          client: 'apiv2',
          query: similarListenersQuery,
          variables: { originator, limit, minScore },
          fetchPolicy: 'network-only',
        });
        const results = (data.similarListeners && data.similarListeners.listeners) || [];
        if (callback) {
          callback(results);
        }
      } catch (e) {
        this.$notify({
          type: 'error',
          message: isNotFoundError(e)
            ? 'This listener has no profile on record to compare against.'
            : 'Could not find similar listeners.',
        });
        if (callback) {
          callback([]);
        }
      }
    },

    async handleApplyInferred({ field, originator }) {
      const apiField = toPromoteField(field);

      try {
        const { data } = await this.$apollo.mutate({
          client: 'apiv2',
          mutation: promoteDemographicsMutation,
          variables: { originator, field: apiField },
        });
        const result = data.promoteDemographics;

        if (result.promoted && Object.keys(result.promoted).length > 0) {
          this.$notify({ type: 'success', message: result.message });
          await this.handleOpenListener({ originator }, { fallback: false });
        } else {
          const reason = (apiField && result.skipped && result.skipped[apiField]) || 'not eligible';
          this.$notify({ type: 'warning', message: `Could not promote: ${reason}` });
        }
      } catch (e) {
        this.$notify({ type: 'error', message: 'Could not promote demographic.' });
      }
    },

    async handleReplaceDemographic({ field, originator }) {
      try {
        await this.$apollo.mutate({
          client: 'apiv2',
          mutation: replaceDemographicMutation,
          variables: { originator, field },
        });
        this.$notify({
          type: 'success',
          message: field ? 'Declared value replaced.' : 'Declared values replaced.',
        });
        await this.handleOpenListener({ originator }, { fallback: false });
      } catch (e) {
        this.$notify({ type: 'error', message: 'Could not replace the declared value.' });
      }
    },

    async handleReviewLink({ originator, type, value, action }) {
      const done = {
        confirm: 'Marked as the same person.',
        dismiss: 'Suggestion dismissed.',
        reset: 'Back to an unreviewed suggestion.',
      };

      try {
        await this.$apollo.mutate({
          client: 'apiv2',
          mutation: reviewLinkedIdentifierMutation,
          variables: { originator, type, value, action },
        });
        this.$notify({ type: 'success', message: done[action] });
        await this.handleOpenListener({ originator }, { fallback: false });
      } catch (e) {
        this.$notify({ type: 'error', message: 'Could not save your decision.' });
      }
    },

    handleDeploy({ channel, segment }) {
      this.$notify({
        type: 'success',
        title: 'Campaign Queued',
        message: `${channel.toUpperCase()} campaign for ${segment} audience will be deployed.`,
        duration: 5000,
      });
    },

    sanitiseParams(params) {
      const MAP = {
        pageSize: 'page_size',
        ageGroup: 'age_group',
        inferredGender: 'inferred_gender',
        inferredAgeGroup: 'inferred_age_group',
        inferredLsm: 'inferred_lsm',
        inferredInterests: 'inferred_interests',
        inferredLanguage: 'inferred_language',
        inferredLocation: 'inferred_location',
        channelType: 'channel_type',
        hasChannel: 'has_channel',
        timeslotId: 'timeslot_id',
        peakTimeOfDay: 'peak_time_of_day',
        lastInteractionAfter: 'last_interaction_after',
        lastInteractionBefore: 'last_interaction_before',
        optIn: 'opt_in',
        audienceId: 'audience_id',
        profileComplete: 'profile_complete',
      };

      const result = {};
      Object.entries(params).forEach(([key, val]) => {
        if (val === null || val === undefined || val === '') {
          return;
        }
        const mapped = MAP[key] || key;
        result[mapped] = val;
      });
      return result;
    },
  },
};
</script>

<style></style>


// WEBPACK FOOTER //
// SegmentationDashboardContainer.vue?7945ae70